import React, { createContext, useContext, useState, useEffect } from "react";
import { login as loginApi, logout as logoutApi, fetchCurrentAdmin } from "../services/authApi";
import { tokenStorage } from "../services/apiClient";

const USER_KEY = "spotdeal_admin_user";
const AuthContext = createContext(null);

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isAuthError(err) {
  return err?.status === 401 || err?.status === 403;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      const token = tokenStorage.getAccessToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const cached = readCachedUser();
      if (cached) {
        setUser(cached);
      }

      try {
        const profile = await fetchCurrentAdmin();
        localStorage.setItem(USER_KEY, JSON.stringify(profile));
        setUser(profile);
      } catch (err) {
        if (isAuthError(err)) {
          // Token may still be valid for other admin APIs; only force logout when nothing cached.
          if (!cached) {
            tokenStorage.clear();
            setUser(null);
          }
        } else if (!cached) {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);

  const login = async (email, password) => {
    const loggedInUser = await loginApi(email, password);
    localStorage.setItem(USER_KEY, JSON.stringify(loggedInUser));
    setUser(loggedInUser);
    return loggedInUser;
  };

  const logout = async () => {
    await logoutApi();
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  const isAuthenticated = !!user && !!tokenStorage.getAccessToken();

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
